import React, { useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

type MaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: MaxWidth;
  maxHeight?: string;
  isLoading?: boolean;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  portalTarget?: Element | null;
}

interface ModalTitleProps {
  children: React.ReactNode;
  showCloseButton?: boolean;
  onClose?: () => void;
  isLoading?: boolean;
  id?: string;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
}

interface ModalActionsProps {
  children: React.ReactNode;
  className?: string;
  justify?: 'start' | 'center' | 'end' | 'between';
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth = 'md',
  maxHeight = '80vh',
  isLoading = false,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  portalTarget,
}) => {
  // Get or create modal root element
  const modalRoot = useMemo(() => {
    if (portalTarget) return portalTarget;

    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    return root;
  }, [portalTarget]);
  // Memoize max width classes to avoid recreation on every render
  const maxWidthClasses = useMemo(
    () => ({
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
    }),
    [],
  );

  // Memoize close handler to prevent unnecessary re-renders
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  // Track if the mouse down event started on the backdrop
  const backdropMouseDownRef = React.useRef(false);

  // Handle backdrop mouse down
  const handleBackdropMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only set flag if the mouse down started directly on the backdrop
    if (e.target === e.currentTarget) {
      backdropMouseDownRef.current = true;
    }
  }, []);

  // Handle backdrop click (mouse up)
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only close if both mouse down and mouse up happened on the backdrop
      if (e.target === e.currentTarget && closeOnBackdropClick && backdropMouseDownRef.current) {
        handleClose();
      }
      // Reset the flag after handling the click
      backdropMouseDownRef.current = false;
    },
    [closeOnBackdropClick, handleClose],
  );

  // Handle escape key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape && isOpen) {
        handleClose();
      }
    },
    [closeOnEscape, handleClose, isOpen],
  );

  // Add/remove event listeners for escape key
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      // Reset backdrop click flag when modal closes
      backdropMouseDownRef.current = false;
    }
  }, [isOpen, handleKeyDown]);

  // Early return for better performance when modal is closed
  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      <div
        className={`bg-white rounded-lg w-full ${maxWidthClasses[maxWidth]} flex flex-col shadow-2xl`}
        style={{ maxHeight }}
        role="document"
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            // Pass modal props to title component
            if (child.type === ModalTitle) {
              return React.cloneElement(child as React.ReactElement<ModalTitleProps>, {
                showCloseButton: showCloseButton,
                onClose: handleClose,
                isLoading: isLoading,
              });
            }
          }
          return child;
        })}
      </div>
    </div>
  );

  // Render modal using React Portal
  return createPortal(modalContent, modalRoot);
};

const ModalTitle: React.FC<ModalTitleProps> = ({
  children,
  showCloseButton = true,
  onClose,
  isLoading,
  id = 'modal-title',
}) => (
  <>
    <div className="flex justify-between items-center p-6 pb-4">
      <h2 id={id} className="text-xl font-bold text-gray-900 flex-1 pr-4">
        {children}
      </h2>
      {showCloseButton && (
        <button
          onClick={onClose}
          className="text-gray-400 w-8 h-8 items-center hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1 transition-colors text-2xl leading-none flex-shrink-0"
          disabled={isLoading}
          type="button"
          aria-label="Close modal"
        >
          &times;
        </button>
      )}
    </div>
    <hr className="border-gray-200" />
  </>
);

const ModalHeader: React.FC<ModalHeaderProps> = ({ children, className = '' }) => (
  <>
    <div className={`px-6 py-4 bg-gray-50 ${className}`}>{children}</div>
    <hr className="border-gray-200" />
  </>
);

const ModalContent: React.FC<ModalContentProps> = ({ children, className = '', id = 'modal-content' }) => (
  <div id={id} className={`flex-1 overflow-y-auto px-6 py-4 ${className}`}>
    {children}
  </div>
);

const ModalActions: React.FC<ModalActionsProps> = ({ children, className = '', justify = 'end' }) => {
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  };

  return (
    <>
      <hr className="border-gray-200" />
      <div className={`px-6 py-4 rounded-b-lg bg-gray-50 flex ${justifyClasses[justify]} space-x-3 ${className}`}>
        {children}
      </div>
    </>
  );
};

// Export all components with proper display names for debugging
Modal.displayName = 'Modal';
ModalTitle.displayName = 'ModalTitle';
ModalHeader.displayName = 'ModalHeader';
ModalContent.displayName = 'ModalContent';
ModalActions.displayName = 'ModalActions';

export default Modal;
export { ModalTitle, ModalHeader, ModalContent, ModalActions };
export type { ModalProps, ModalTitleProps, ModalHeaderProps, ModalContentProps, ModalActionsProps };
